import React, { useCallback } from "react";
import { Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import { formatNutritionPlanUpdatedDate } from "../nutritionPlanDateUtils";
import {
  buildNutritionPlanLibrarySearchResults,
  getNutritionPlanDisplayName,
} from "../nutritionPlanSearch";
import { formatCalorieValue } from "../../shared/nutritionFormatters";
import ClientNutritionSelectionModal, {
  clientNutritionSelectionModalStyles as styles,
} from "./ClientNutritionSelectionModal";

function NutritionLibraryResultRow({
  isHovered,
  isSelected,
  isMobile,
  language,
  plan,
  t,
  onHover,
  onPress,
  onLayout,
}) {
  const displayName =
    getNutritionPlanDisplayName(plan, language) || t("nutritionPlans-string");
  const updatedDateLabel = formatNutritionPlanUpdatedDate(plan?.updatedAt, language);
  const updatedMetaLabel = updatedDateLabel
    ? t("nutritionPlanUpdatedDate-string", {
        defaultValue: "Updated {{date}}",
        date: updatedDateLabel,
      })
    : "";
  const kcalLabel = `${formatCalorieValue(plan?.calories)} kcal`;

  return (
    <TouchableOpacity
      activeOpacity={0.96}
      onLayout={onLayout}
      onMouseEnter={!isMobile ? onHover : undefined}
      onPress={onPress}
      style={styles.resultRowTouchable}
    >
      <View
        style={[
          styles.resultRow,
          isHovered && !isSelected ? styles.resultRowHover : null,
          isSelected ? styles.resultRowSelected : null,
        ]}
      >
        <View style={styles.resultMain}>
          <View style={styles.resultIconWrap}>
            <Ionicons name="reader-outline" size={16} color={Colors.grayColor} />
          </View>
          <Text numberOfLines={1} style={styles.resultName}>
            {displayName}
          </Text>
          <Text numberOfLines={1} style={styles.resultKcalText}>
            · {kcalLabel}
          </Text>
        </View>

        {updatedMetaLabel ? (
          <View>
            <Text numberOfLines={1} style={styles.resultUpdatedText}>
              {updatedMetaLabel}
            </Text>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
}

export default function ClientNutritionPlanImportModal({
  athleteName = "",
  isVisible = false,
  isLoading = false,
  plans = [],
  sourceFolderKey = null,
  onClose,
  onSelectPlan,
}) {
  const { t } = useTranslation();
  const buildSearchResults = useCallback(
    (sourcePlans, searchText, language) =>
      buildNutritionPlanLibrarySearchResults(sourcePlans, searchText, language),
    [],
  );
  const handleSelectPlan = useCallback(
    (plan) => onSelectPlan?.(plan, sourceFolderKey),
    [onSelectPlan, sourceFolderKey],
  );

  return (
    <ClientNutritionSelectionModal
      badgeIconName="person-outline"
      badgeText={athleteName}
      buildSearchResults={buildSearchResults}
      emptyState={({ items }) => ({
        iconName:
          Array.isArray(items) && items.length > 0 ? "search-outline" : "reader-outline",
        mainText:
          Array.isArray(items) && items.length > 0
            ? "noNutritionPlansFound-string"
            : "nutritionPlanLibraryEmptyTitle-string",
        subText:
          Array.isArray(items) && items.length > 0
            ? "adjustFiltersOrSearch-string"
            : "clientNutritionImportEmptyLibraryDescription-string",
      })}
      eyebrowText={t("importFromLibrary-string", "Import from library")}
      isLoading={isLoading}
      isVisible={isVisible}
      items={plans}
      loadingTitle={t(
        "clientNutritionImportLoadingTitle-string",
        "Loading library nutrition plans",
      )}
      onClose={onClose}
      onSelectItem={handleSelectPlan}
      renderItemRow={({ item, ...rowProps }) => (
        <NutritionLibraryResultRow
          key={String(item?.id ?? rowProps.index)}
          plan={item}
          {...rowProps}
        />
      )}
      searchAccessibilityLabel={t(
        "clientNutritionImportSearchPlaceholder-string",
        "Search nutrition plans...",
      )}
      searchPlaceholder={t(
        "clientNutritionImportSearchPlaceholder-string",
        "Search nutrition plans...",
      )}
    />
  );
}
